import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { reconciliationAPI } from '../../../../api';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { Card, CardContent } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import {
  AlertTriangle,
  Upload,
  Inbox,
  CheckCircle2,
  Wallet,
  BarChart3,
  Scale,
  ArrowRight,
} from 'lucide-react';

/**
 * Reconciliation hub (plan §8 + Phase 2 dashboard).
 *
 * Grid of every reconcilable account. Each card shows:
 *   - account name + type + ledger class
 *   - Statement balance   (running balance from latest CSV line)
 *   - System balance      (SparxMate's current_balance)
 *   - Difference          (statement - system; 0 = in sync)
 *   - Unmatched count     (queue = unmatched/suggested/partially/needs_review)
 *   - Last import date
 *
 * Fetched in a single roundtrip via `/reconciliation/account-stats` so the
 * page stays fast with many accounts.
 */
function formatMoney(n, { signed = false } = {}) {
  const num = Number(n ?? 0);
  const formatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    signDisplay: signed ? 'exceptZero' : 'auto',
  });
  return formatter.format(num);
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

const LEDGER_LABELS = {
  asset_bank: 'Bank / cash',
  liability: 'Liability',
  equity_drawings: 'Drawings',
};

// Anything within this tolerance is "in sync" -- mirrors the backend
// rounding tolerance so the UI doesn't flag a 1p drift.
const DIFFERENCE_TOLERANCE = 0.01;

export default function ReconciliationHub() {
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await reconciliationAPI.getAccountStats();
      setStats(res.stats || []);
      setSummary(res.summary || null);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load reconciliation stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totalUnmatched = useMemo(
    () => (summary?.queue_count ?? stats.reduce((acc, s) => acc + (s.queue_count || 0), 0)),
    [stats, summary]
  );
  const totalReconciled = useMemo(
    () =>
      summary?.reconciled_count ?? stats.reduce((acc, s) => acc + (s.reconciled_count || 0), 0),
    [stats, summary]
  );
  const accountsWithDifference = useMemo(
    () =>
      summary?.accounts_with_difference ??
      stats.filter(
        (s) => s.difference != null && Math.abs(Number(s.difference)) > DIFFERENCE_TOLERANCE
      ).length,
    [stats, summary]
  );

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Reconciliation
            </h1>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Import bank, card and loan statements, match them against your SparxMate
              records, and keep your accounting in sync with reality.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/tenant/banking')}>
              Back to banking
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/tenant/banking/reconciliation/status-report')}
            >
              <BarChart3 className="mr-1 h-4 w-4" /> Status report
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/tenant/banking/reconciliation/import')}
            >
              <Upload className="mr-1 h-4 w-4" /> Import statement
            </Button>
          </div>
        </div>

        {error && (
          <Card className="mb-4 border-red-400 bg-red-50 dark:bg-red-950/30">
            <CardContent className="flex gap-2 p-4 text-xs text-red-700 dark:text-red-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="text-sm text-gray-500">Loading accounts…</div>
        ) : stats.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-sm text-gray-500">
              No reconcilable accounts. Create a bank, card or loan account from the{' '}
              <button
                type="button"
                onClick={() => navigate('/tenant/banking')}
                className="text-blue-600 hover:underline"
              >
                banking page
              </button>{' '}
              to get started.
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
              <SummaryCard
                icon={<Wallet className="h-4 w-4" />}
                label="Reconcilable accounts"
                value={stats.length}
              />
              <SummaryCard
                icon={<Inbox className="h-4 w-4" />}
                label="Lines awaiting action"
                value={totalUnmatched}
                tone={totalUnmatched > 0 ? 'warn' : 'good'}
              />
              <SummaryCard
                icon={<CheckCircle2 className="h-4 w-4" />}
                label="Reconciled lines"
                value={totalReconciled}
                tone="good"
              />
              <SummaryCard
                icon={<Scale className="h-4 w-4" />}
                label="Accounts out of sync"
                value={accountsWithDifference}
                tone={accountsWithDifference > 0 ? 'warn' : 'good'}
              />
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {stats.map((s) => (
                <AccountCard
                  key={s.id}
                  stat={s}
                  onOpen={() => navigate(`/tenant/banking/reconciliation/accounts/${s.id}`)}
                  onImport={() =>
                    navigate(`/tenant/banking/reconciliation/accounts/${s.id}/import`)
                  }
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function AccountCard({ stat, onOpen, onImport }) {
  const ledger = LEDGER_LABELS[stat.ledger_class] || stat.ledger_class || '—';
  const statementBalance = stat.statement_balance;
  const systemBalance = stat.current_balance;
  const difference = stat.difference;
  const queueCount = stat.queue_count || 0;
  const reconciledCount = stat.reconciled_count || 0;
  const hasStatementBalance = statementBalance != null;
  const inSync =
    difference != null && Math.abs(Number(difference)) <= DIFFERENCE_TOLERANCE;

  const differenceTone = !hasStatementBalance
    ? 'text-gray-400'
    : inSync
      ? 'text-green-700 dark:text-green-400'
      : 'text-amber-700 dark:text-amber-400';

  return (
    <Card className="cursor-pointer transition-shadow hover:shadow-md">
      <CardContent className="p-4" onClick={onOpen}>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {stat.account_name}
              {stat.owner_label ? (
                <span className="ml-1 text-gray-400">· {stat.owner_label}</span>
              ) : null}
            </div>
            <div className="text-[11px] uppercase tracking-wide text-gray-500">
              {stat.account_type?.replace(/_/g, ' ')} · {ledger}
            </div>
          </div>
          {queueCount > 0 ? (
            <Badge variant="warning">{queueCount} to match</Badge>
          ) : stat.total_lines > 0 ? (
            <Badge variant="success">In sync</Badge>
          ) : null}
        </div>

        <div className="mb-3 grid grid-cols-3 gap-2 rounded-md border border-gray-100 bg-gray-50 p-2 text-[11px] dark:border-gray-800 dark:bg-gray-900/40">
          <BalanceCell
            label="Statement"
            value={hasStatementBalance ? formatMoney(statementBalance) : '—'}
            hint={
              stat.statement_balance_as_of
                ? `as of ${formatDate(stat.statement_balance_as_of)}`
                : 'no imports yet'
            }
          />
          <BalanceCell
            label="System"
            value={formatMoney(systemBalance)}
            hint="SparxMate"
          />
          <BalanceCell
            label="Difference"
            value={hasStatementBalance ? formatMoney(difference, { signed: true }) : '—'}
            hint={
              !hasStatementBalance ? '' : inSync ? 'in sync' : 'needs attention'
            }
            valueClassName={differenceTone}
          />
        </div>

        <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
          <MiniCount
            label="Queue"
            value={queueCount}
            tone={queueCount > 0 ? 'warn' : 'muted'}
          />
          <MiniCount label="Matched" value={stat.matched_count || 0} />
          <MiniCount
            label="Reconciled"
            value={reconciledCount}
            tone={reconciledCount > 0 ? 'good' : 'muted'}
          />
        </div>

        {Number(stat.uncleared_amount) > 0 && (
          <div className="mb-3 text-[11px] text-amber-700 dark:text-amber-400">
            {formatMoney(stat.uncleared_amount)} uncleared
          </div>
        )}

        <div className="mb-3 text-[11px] text-gray-500">
          {stat.last_import_at ? (
            <>Last import: {formatDate(stat.last_import_at)}</>
          ) : (
            <>No statement imported yet.</>
          )}
        </div>

        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" onClick={onOpen}>
            Open workbench <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
          <Button size="sm" onClick={onImport}>
            <Upload className="mr-1 h-3 w-3" /> Import
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BalanceCell({ label, value, hint, valueClassName = '' }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`font-mono text-[12px] font-semibold ${valueClassName}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-[9px] text-gray-400">{hint}</div> : null}
    </div>
  );
}

function MiniCount({ label, value, tone }) {
  const toneClass =
    tone === 'good'
      ? 'text-green-700 dark:text-green-400'
      : tone === 'warn'
        ? 'text-amber-700 dark:text-amber-400'
        : tone === 'muted'
          ? 'text-gray-500'
          : '';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

function SummaryCard({ icon, label, value, tone }) {
  const toneClass =
    tone === 'good'
      ? 'text-green-700 dark:text-green-400'
      : tone === 'warn'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-gray-900 dark:text-white';
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {icon}
          <span>{label}</span>
        </div>
        <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
